'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';

import { ReportListingFormValues, reportListingSchema } from '@/slw/entities/listing/listing.schema';
import { Button } from '@/slw/shared/ui/button';
import { Dialog, DialogContent } from '@/slw/shared/ui/dialog';
import FormProvider, { Input, Select, Textarea } from '@/slw/shared/ui/form';
import { useReasonReportListing } from '@/slw/entities/buyer/listing';
import { useAuthStore } from '@/slw/shared/stores';

export function ReportListingModal({
  open,
  onOpenChange,
  onSubmitReportListing,
  isLoading,
}: {
  open: boolean;
  isLoading?: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmitReportListing?: (values: ReportListingFormValues) => void;
}) {
  const { user } = useAuthStore();
  const form = useForm<ReportListingFormValues>({
    resolver: zodResolver(reportListingSchema),
    defaultValues: {
      fullName: `${user?.firstName} ${user?.lastName}` || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      reason: '',
      description: '',
    },
  });

  const { data: reasonData } = useReasonReportListing();

  const onSubmit = (data: ReportListingFormValues) => {
    onSubmitReportListing?.(data);
  };

  useEffect(() => {
    form.reset({
      fullName: `${user?.firstName} ${user?.lastName}` || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      reason: '',
      description: '',
    });
  }, [form, open, user?.email, user?.firstName, user?.lastName, user?.phoneNumber]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='md:max-w-[668px] overflow-y-scroll gap-4 max-h-screen md:max-h-[900px]'
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <h2 className='text-2xl font-bold mt-3'>Report Listing</h2>
        <FormProvider {...form} onSubmit={onSubmit}>
          <div className='flex flex-col gap-4'>
            <Input name='fullName' label='Full name' required placeholder='Enter your name' direction='column' />
            <Input name='email' label='Email' required placeholder='Enter your email' direction='column' type='email' />
            <Input name='phoneNumber' label='Phone number' placeholder='Enter your phone number' direction='column' />
            <Select
              name='reason'
              label='Your report reason'
              required
              options={
                reasonData?.map((item) => ({
                  label: item.name,
                  value: item.code,
                })) || []
              }
              placeholder='Choose your reason'
              direction='column'
            />
            <Textarea
              name='description'
              label='More description'
              required
              placeholder='More description'
              direction='column'
              rows={4}
            />
          </div>
          <Button type='submit' color='primary' variant='default' className='w-full mt-4' disabled={isLoading}>
            {isLoading ? 'Submitting...' : 'SUBMIT'}
          </Button>
        </FormProvider>
        <p className='text-sm text-[#979696]'>
          Per our{' '}
          <Link href='/privacy-policy' className='text-[#B6864C] underline'>
            Privacy Policy
          </Link>
          , the information provided will only be used to ensure delivery.
          <br />
          Using this form to send spam is a violation of our{' '}
          <Link href='/terms-and-conditions' className='text-[#B6864C] underline'>
            Terms and Conditions
          </Link>
          .
        </p>
      </DialogContent>
    </Dialog>
  );
}
